import React, { FC, useState, useEffect } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { IconProp } from '@fortawesome/fontawesome-svg-core'
import { useTranslation } from 'react-i18next'
import { useHistory, useParams } from 'react-router-dom'

import Loading from '../common/Loading'

import { LangValue } from '../../../states/types/common'
import { ApplicationTimeLine } from '../../../states/types/member'
import TransAppName from './TransAppName'
import { convertHexColor, hex2rgb } from '../../../utils/color'
import { TeamReportTimeLine } from '../../../states/types/team'
import { URL } from '../../constants/url'
import { combineYearMonthAndDay, endOfMonth, startOfMonth } from '../../../utils/date'
import { setPath } from '../../../utils/url'
import { ReactSVG } from 'react-svg'
import { uniqueId } from 'lodash'

interface ApplicationTimeLineProps {
  isLoading: boolean | undefined
  applicationTimeLine: ApplicationTimeLine[] | TeamReportTimeLine[] | undefined
  categoryFilter: string | undefined
  applicationFilter: string | undefined
  setInitialFilter: () => void
  mode: "daily" | "monthly" | "custom"
  // eslint-disable-next-line react/require-default-props
  date?: string | { start_date: string, end_date: string }
}

const ApplicationTimeLine: FC<ApplicationTimeLineProps> = (props) => {
  const { t } = useTranslation()
  const { language } = useParams<{ language: LangValue }>()
  const history = useHistory()

  const {
    isLoading,
    applicationTimeLine,
    categoryFilter,
    applicationFilter,
    setInitialFilter,
    mode,
    date,
  } = props

  const DISPLAY_LIMIT = 100
  const [displayTime, setDisplayTime] = useState<ApplicationTimeLine[]>([])
  const [stateCategoryFilter, setStateCategoryFilter] = useState<ApplicationTimeLine | undefined>(undefined)
  const [stateApplicationFilter, setStateApplicationFilter] = useState<ApplicationTimeLine | undefined>(undefined)

  let count = 1

  useEffect(() => {
    if (!applicationTimeLine) return

    const tmpList = applicationTimeLine.map((item) => ({ ...item, sum_elapsed_time: Math.floor(item.sum_elapsed_time / 60) }))
    tmpList.sort((a, b) => {
      if (a.sum_elapsed_time < b.sum_elapsed_time) return 1
      if (a.sum_elapsed_time > b.sum_elapsed_time) return -1
      if (a.sum_keydown < b.sum_keydown) return 1
      if (a.sum_keydown > b.sum_keydown) return -1
      if (a.count < b.count) return 1
      if (a.count > b.count) return -1
      return 0
    })
    setDisplayTime(tmpList)

    const category = applicationTimeLine.find((item) => t(`parts.category_name.${item.category_name}`) === categoryFilter)
    setStateCategoryFilter(category)

    if (applicationFilter === t('parts.app_name.other')) return
    const application = applicationTimeLine.find((item) => item.name.toLowerCase() === applicationFilter?.toLowerCase())
    setStateApplicationFilter(application)
  }, [categoryFilter, applicationFilter, applicationTimeLine])

  const renderApplicationIcon = () => {
    if (!stateApplicationFilter) return (<></>)

    const icon = stateApplicationFilter.icon.split(" ")
    return (
      <FontAwesomeIcon
        icon={{
          prefix: icon[0],
          iconName: icon[1].slice(3),
        } as IconProp}
        className="application-filter-icon"
      />
    )
  }

  const transitionSearch = (word: string) => {
    if (!date) return
    let start_date
    let end_date
    if (mode === 'daily' && typeof date === "string") {
      start_date = date
      end_date = date
    } else if (mode === 'monthly' && typeof date === "string") {
      start_date = startOfMonth(combineYearMonthAndDay(date, 1), "YYYY-MM-DD")
      end_date = endOfMonth(combineYearMonthAndDay(date, 1), "YYYY-MM-DD")
    } else if (mode === 'custom' && typeof date !== "string") {
      start_date = date.start_date
      end_date = date.end_date
    }
    history.push(`${setPath(URL.SEARCH.path, language)}?start_date=${start_date}&end_date=${end_date}&search_word=${word}`)
  }

  return (
    <div className="box" style={{ background: "#f5f5f5", padding: "16px 0" }}>
      <div style={{ textAlign: "center", paddingTop: "1.5em", paddingBottom: "1em" }}>
        <span style={{ color: "#094E8D", fontSize: "24px", fontWeight: "bold" }}>{t("parts.application_time_line.title")}</span>
      </div>
      {
        isLoading || !applicationTimeLine ? (
          <Loading />
        ) : (
          <>
            {
              stateCategoryFilter && (
                <div
                  className="flex"
                  style={{
                    background: "white",
                    marginBottom: "1em",
                    borderTop: "1px solid rgba(51,51,51,0.35)",
                    borderBottom: "1px solid rgba(51,51,51,0.35)",
                  }}
                >
                  <div style={{ padding: "0.75em" }}>
                    <div
                      className="category_circle"
                      style={{ ['--color' as string]: hex2rgb(stateCategoryFilter.category_color) }}
                    >
                      <img
                        src={`/static/images/report/icon_category_${stateCategoryFilter.icon_path}.svg`}
                        alt={stateCategoryFilter.icon_path}
                      />
                    </div>
                  </div>
                  <div style={{ padding: "0.75em", paddingLeft: "0em" }}>
                    <p style={{ fontSize: "18px" }}>
                      {
                        language === "en" ? (
                          <>
                            {t("parts.application_time_line.filter")}
                            &nbsp;
                            {t(`parts.category_name.${stateCategoryFilter.category_name}`)}
                            &nbsp;
                            Category
                          </>
                        ) : (
                          <>
                            {t(`parts.category_name.${stateCategoryFilter.category_name}`)}
                            分類
                            {t("parts.application_time_line.filter")}
                          </>
                        )
                      }
                    </p>
                    <button
                      type="button"
                      className="button-vanilla"
                      style={{ background: "#094E8D", borderRadius: "4px" }}
                      onClick={() => setInitialFilter()}
                    >
                      <div style={{ paddingLeft: "0.25em", paddingRight: "0.25em", paddingTop: "0.5em", paddingBottom: "0.5em" }}>
                        <span style={{ fontSize: "14px", fontWeight: "bold", color: "white" }}>
                          {t("parts.application_time_line.release_filter")}
                        </span>
                      </div>
                    </button>
                  </div>
                </div>
              )
            }

            {
              stateApplicationFilter && (
                <div
                  className="flex"
                  style={{
                    background: "white",
                    marginBottom: "1em",
                    borderTop: "1px solid rgba(51,51,51,0.35)",
                    borderBottom: "1px solid rgba(51,51,51,0.35)",
                  }}
                >
                  <div style={{ padding: "0.75em" }}>
                    <div
                      style={{
                        width: "3.25em",
                        height: "3.25em",
                        borderRadius: "50%",
                        textAlign: "center",
                        background: convertHexColor(stateApplicationFilter.color, 0.8),
                      }}
                    >
                      <div style={{ width: "3.25em", height: "3.25em", display: "table-cell", verticalAlign: "middle", textAlign: "center" }}>
                        {renderApplicationIcon()}
                      </div>
                    </div>
                  </div>
                  <div style={{ padding: "0.75em", paddingLeft: "0em" }}>
                    <p style={{ fontSize: "18px" }}>
                      {
                        language === "en" ? (
                          <>
                            {t("parts.application_time_line.filter")}
                            &nbsp;
                            {stateApplicationFilter.name}
                          </>
                        ) : (
                          <>
                            {stateApplicationFilter.name}
                            {t("parts.application_time_line.filter")}
                          </>
                        )
                      }
                    </p>
                    <button
                      type="button"
                      id="application-filter-release"
                      className="button-vanilla"
                      style={{ background: "#094E8D", borderRadius: "4px" }}
                      onClick={() => setInitialFilter()}
                    >
                      <div style={{ paddingLeft: "0.25em", paddingRight: "0.25em", paddingTop: "0.5em", paddingBottom: "0.5em" }}>
                        <span style={{ fontSize: "14px", fontWeight: "bold", color: "white" }}>
                          {t("parts.application_time_line.release_filter")}
                        </span>
                      </div>
                    </button>
                  </div>
                </div>
              )
            }

            {
              displayTime.map((item) => {
                if (count < DISPLAY_LIMIT) {
                  if (
                    (stateCategoryFilter && stateCategoryFilter.category_name !== item.category_name)
                    || (stateApplicationFilter && stateApplicationFilter.name !== item.name)
                  ) {
                    return <React.Fragment key={uniqueId('category_')} />
                  }

                  count += 1
                  return (
                    <div
                      key={uniqueId('category_')}
                      className="time-line-box"
                      style={{ background: "white", margin: "0.25em" }}
                    >
                      <div className="flex">
                        <div style={{ padding: "0.75em" }}>
                          <div style={{ width: "3.25em", height: "3.25em", background: convertHexColor(item.color, 0.8), borderRadius: "50%" }}>
                            <div style={{ width: "3.25em", height: "3.25em", display: "table-cell", verticalAlign: "middle", textAlign: "center" }}>
                              <i className={item.icon} style={{ fontSize: "25px", color: "#FFFFFF", verticalAlign: "middle" }} />
                            </div>
                          </div>
                        </div>
                        <div
                          className="flex"
                          style={{
                            padding: "0.75em",
                            paddingLeft: "0em",
                            paddingRight: "0.5em",
                            width: "100%",
                            flexDirection: "column",
                            justifyContent: "center",
                            alignItems: "flex-start",
                          }}
                        >
                          <div style={{ display: "flex", alignItems: "center" }}>
                            <span style={{ fontWeight: "bold", fontSize: "18px" }}><TransAppName appName={item.name ?? item.process_name} /></span>
                            {/* <button
                              type="button"
                              className="report_search_button"
                              onClick={() => transitionSearch(item.name ?? item.process_name)}
                            >
                              <ReactSVG
                                src="/static/images/sidebar/icon-search.svg"
                                className="icon"
                              />
                            </button> */}
                          </div>
                          {
                            (item.site_name && item.domain_name !== item.title) && (
                              <div style={{ wordWrap: "break-word" }}>
                                <span style={{ fontWeight: "bold", fontSize: "16px" }}>{item.title}</span>
                              </div>
                            )
                          }
                        </div>
                      </div>
                      <div style={{ paddingLeft: "0.75em", paddingRight: "0.75em", wordBreak: "break-word", display: "flex", alignItems: "center" }}>
                        {
                          item.site_name ? (
                            <span style={{ fontWeight: "bold", fontSize: "18px" }}>
                              {
                                item.url ? (
                                  <a href={item.url} target="_blank" rel="noreferrer">
                                    {item.site_name.length <= 40 ? item.site_name : (`${item.site_name.substr(0, 40)}...`)}
                                  </a>
                                ) : (
                                  <>
                                    {item.site_name.length <= 40 ? item.site_name : (`${item.site_name.substr(0, 40)}...`)}
                                  </>
                                )
                              }
                            </span>
                          ) : (
                            <span style={{ fontWeight: "bold", fontSize: "18px" }}>{item.title}</span>
                          )
                        }
                        {
                          (item.title || item.site_name) && (
                            <button
                              type="button"
                              className="report_search_button"
                              onClick={() => transitionSearch(item.title ?? item.site_name)}
                            >
                              <ReactSVG
                                src="/static/images/sidebar/icon-search.svg"
                                className="icon"
                              />
                            </button>
                          )
                        }
                      </div>
                      <div className="flex">
                        <div style={{ padding: "0.75em", width: "50%" }}>
                          <div>
                            <span style={{ fontSize: "14px" }}>
                              {t("parts.application_time_line.working_time")}
                              :
                            </span>
                            <span style={{ paddingLeft: "0.1em", fontWeight: "bold", fontSize: "16px" }}>
                              {item.sum_elapsed_time.toLocaleString()}
                            </span>
                            <span style={{ fontSize: "14px" }}>
                              {t("parts.application_time_line.minute")}
                            </span>
                          </div>
                          <div>
                            <span style={{ fontSize: "14px" }}>
                              {t("parts.application_time_line.biz_point")}
                              :
                            </span>
                            <span style={{ paddingLeft: "0.1em", fontWeight: "bold", fontSize: "16px" }}>
                              {item.sum_keydown.toLocaleString()}
                            </span>
                            <span style={{ fontSize: "14px" }}> biz</span>
                          </div>
                          <div>
                            <span style={{ fontSize: "14px" }}>
                              {t("parts.application_time_line.display_count")}
                              :
                            </span>
                            <span style={{ paddingLeft: "0.1em", fontWeight: "bold", fontSize: "16px" }}>{item.count.toLocaleString()}</span>
                            <span style={{ fontSize: "14px" }}>
                              &nbsp;
                              {t("parts.application_time_line.count")}
                            </span>
                          </div>
                        </div>
                        <div style={{ position: "relative", width: "50%", display: "flex", alignItems: "flex-end" }}>
                          <div
                            className="category_label"
                            style={{ ['--color' as string]: hex2rgb(item.category_color) }}
                          >
                            <div className="title title_label">
                              <img
                                src={`/static/images/report/icon_category_${item.icon_path}.svg`}
                                alt={item.icon_path}
                              />
                              {t(`parts.category_name.${item.category_name}`)}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )
                }
                return <React.Fragment key={uniqueId('category_')} />
              })
            }
            {
              count >= DISPLAY_LIMIT && (
                <div style={{ textAlign: "center", paddingTop: "1em", paddingBottom: "0.5em" }}>
                  {t("parts.application_time_line.limit_message", { limit: DISPLAY_LIMIT })}
                </div>
              )
            }
          </>
        )
      }
    </div>
  )
}

export default ApplicationTimeLine
